<template>
  <div>
    <CCard>
      <CCardHeader>
        <h2>
          <span>プロジェクト管理</span>
          <span class="card-header-actions">
            <router-link :to="{ name: projectRegister }">
              <button class="btn btn-primary">新規登録</button>
            </router-link>
          </span>
        </h2>
      </CCardHeader>
      <CCardBody>
        <CAlert color="info">
          社員を選択すると、その社員の所属プロジェクトを追加・解除できます。
          チェック欄はログイン中の管理者本人ではなく、選択した社員へ反映されます。
        </CAlert>

        <CRow v-if="errors.length">
          <ul>
            <li class="preika-form-message" v-for="error in errors" :key="error">
              {{ error }}
            </li>
          </ul>
        </CRow>

        <CRow class="mb-3">
          <CCol sm="12" md="6" lg="4">
            <label for="project-member-user">所属を変更する社員</label>
            <select
              id="project-member-user"
              class="custom-select"
              v-model="selectedMemberUserId"
              :disabled="membershipSaving"
              @change="loadSelectedMemberProjects"
            >
              <option value="">選択してください</option>
              <option
                v-for="member in memberUsers"
                :key="member.id"
                :value="String(member.id)"
              >
                {{ member.employee_id ? `${member.employee_id} ` : '' }}{{ member.name }}
              </option>
            </select>
          </CCol>
        </CRow>

        <div v-if="selectedMemberUserId" class="mb-3">
          <h5 class="mb-2">
            {{ selectedMemberName }}さんの所属プロジェクト
          </h5>
          <CAlert v-if="!memberProjects.length" color="warning">
            現在所属しているプロジェクトはありません。下の一覧から追加してください。
          </CAlert>
          <CRow v-else class="mb-3">
            <CCol
              v-for="memberProject in memberProjects"
              :key="memberProject.id"
              sm="12"
              md="6"
              lg="4"
              xl="3"
            >
              <CCard
                color="info"
                class="text-white card-select"
                @click="goProject(memberProject.id)"
              >
                <CCardBody class="py-3">
                  <div class="font-weight-bold">{{ memberProject.name }}</div>
                </CCardBody>
              </CCard>
            </CCol>
          </CRow>
        </div>

        <ListSearchBox
          :value="keyword"
          label="プロジェクト名・取引先企業名・先方担当者で絞り込む"
          placeholder="例: 移行支援、株式会社○○、山田"
          :count="keyword ? totalCount : null"
          @search="applyKeyword"
        />

        <!-- 0件は失敗ではないので、赤字の欄（errors）ではなく案内の字で、表のすぐ上に出す。 -->
        <p v-if="emptyNote" class="text-muted">{{ emptyNote }}</p>
        <div class="table-responsive">
          <table class="table table-bordered">
            <thead>
              <tr>
                <th class="text-center">選択社員の所属</th>
                <th scope="col">プロジェクト名</th>
                <th scope="col">取引先企業名</th>
                <th scope="col">先方担当者</th>
                <th scope="col">プロジェクト責任者</th>
                <th scope="col">契約形態</th>
                <th scope="col">精算時間</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <template v-for="item in project">
              <tr :key="item.id">
                <td class="text-center">
                  <label class="custom-switch mb-0">
                    <input
                      type="checkbox"
                      class="custom-switch-input"
                      :id="`member-prj-${item.id}`"
                      :checked="isMemberAssigned(item.id)"
                      :disabled="!selectedMemberUserId || membershipSaving"
                      @change="onMemberToggle(item, $event.target.checked)"
                    />
                    <span class="custom-switch-indicator"></span>
                  </label>
                </td>
                <td>
                  <router-link
                    :to="{ name: projectInfo, params: { value: item } }"
                  >{{ item.name }}</router-link>
                </td>
                <td>{{ item.company_name }}</td>
                <td>{{ item.staff_name }}</td>
                <td>{{ item.private_staff_name }}</td>
                <td>{{ item.contract }}</td>
                <td>{{ item.base_time }}</td>
                <td>
                  <button
                    @click="openModal(item)"
                    class="btn btn-danger"
                  >
                    削除
                  </button>
                </td>
              </tr>
              <!--
                所属の切り替えを断られた理由は、切り替えた行のすぐ下に出す。
                一覧の上に出していたので、スイッチが元へ戻るのは見えても理由が見えなかった。
              -->
              <tr v-if="rowMessages[item.id]" :key="'message-' + item.id" class="row-result">
                <td colspan="8">
                  <ActionResultMessage :text="rowMessages[item.id].text" :type="rowMessages[item.id].type" />
                </td>
              </tr>
              </template>
            </tbody>
          </table>
        </div>
        <!--
          削除の確認は表の外に1つだけ置く。行の中に置いていたので、
          開くと行の数だけ同じ小窓が重なって描かれていた。
        -->
        <ProjectDeleteModal
          v-if="showModal"
          :draggable="true"
          :resizable="true"
          :val="postItem"
          :loading="loading"
          @delete="deleteProject"
          @close="closeModal"
        />

        <div class="d-flex justify-content-center">
          <VPagination
            v-model="currentPage"
            :click-handler="changePage"
            :page-count="totalPages"
            :force-page="currentPage"
            :force-ellipses="true"
            :container-class="'pagination'"
            :page-class="'page-item'"
            :prev-text="'<'"
            :next-text="'>'"
            :prev-link-class="'page-link'"
            :next-link-class="'page-link'"
            :page-link-class="'page-link'"
            :class-prefix="'pagination'"
          />
        </div>
        <!--
          **戻せない人には出さない。**節が叩く /projectDeleted は
          PROJECT_MANAGE が要る。一覧は PROJECT_EDIT でも開くので、
          編集までの人にはここで必ず赤い帯が出ていた。
        -->
        <DeletedRowsPanel
          v-if="canManageProjects"
          label="削除した案件"
          list-url="/projectDeleted"
          restore-url="/projectRestore"
          :restore-in-body="true"
          :columns="[
            { key: 'name', label: '案件名' },
            { key: 'companyName', label: '取引先' },
            { key: 'dailyCount', label: '勤務表の日数', suffix: '日' },
            { key: 'updatedUser', label: '最後に触った人' }
          ]"
          empty-message="削除した案件はありません。"
          note="戻すのは案件だけです。所属していた人は戻りません。取引先ごと削除されている場合は、先に取引先を戻してください。"
          @restored="reloadAfterRestore"
        />
      </CCardBody>
    </CCard>

    <div
      v-if="globalLoading || membershipSaving"
      class="loading-overlay"
      aria-live="polite"
      aria-busy="true"
    >
      <CSpinner color="primary" style="width: 3rem; height: 3rem;" />
    </div>
  </div>
</template>

<script>
import DeletedRowsPanel from '@/components/DeletedRowsPanel'
import { getAccessContext } from '../services/accessContext'
import { safeErrorDigest } from '../utils/safeResponseMessage'
import axios from 'axios'
import ConstList from './../ConstList'
import { requestFailureMessage } from '@/utils/requestFailure'
import ProjectDeleteModal from './ProjectDeleteModal'
import VPagination from 'vuejs-paginate'
import ListSearchBox from '@/common/ListSearchBox'
import ActionResultMessage from '@/components/ActionResultMessage.vue'

export default {
  name: 'ProjectList',
  components: {
    DeletedRowsPanel,
    ProjectDeleteModal,
    VPagination,
    ListSearchBox,
    ActionResultMessage
  },
  data () {
    return {
      globalLoading: false,
      membershipSaving: false,
      showModal: false,
      errors: [],
      // 0件のときの案内（失敗ではないので errors には入れない）
      emptyNote: '',
      // 所属の切り替えの結果。切り替えた行のすぐ下に出す（キーはプロジェクトのid）。
      rowMessages: {},
      project: [],
      memberUsers: [],
      selectedMemberUserId: '',
      memberProjects: [],
      postItem: null,
      project_id: '',
      projectRegister: '',
      projectInfo: '',
      currentPage: 1,
      pageSize: 10,
      totalPages: 1,
      /* いま効いている絞り込みの言葉。空なら絞り込んでいない。 */
      keyword: '',
      totalCount: 0,
      submit_flg: false,
      loading: false,
      accessContext: null
    }
  },

  computed: {
    /*
     * **削除した案件を戻せるのは PROJECT_MANAGE を持つ人だけ。**
     *
     * 一覧そのものは PROJECT_EDIT でも開く（ApiAccessPolicyService の
     * /project の分岐）。ところが /projectDeleted と /projectRestore は
     * PROJECT_MANAGE を要求する。だから編集までの人がこの画面を開くと、
     * 削除の節が開いた瞬間に必ず取得へ失敗して赤い帯が出る。
     */
    canManageProjects () {
      const permissions = this.accessContext
        && Array.isArray(this.accessContext.permissions)
        ? this.accessContext.permissions
        : []
      const features = this.accessContext
        && Array.isArray(this.accessContext.features)
        ? this.accessContext.features
        : []
      return features.includes('PROJECT')
        && permissions.includes('PROJECT_MANAGE')
    },
    selectedMemberName () {
      const member = this.memberUsers.find(
        item => Number(item.id) === Number(this.selectedMemberUserId)
      )
      return member ? member.name : '選択中の社員'
    }
  },

  created () {
    this.projectRegister = ConstList.ROUTE_PROJECT_REGISTER
    this.projectInfo = ConstList.ROUTE_PROJECT_INFO
  },

  mounted () {
    this.loadAccessContext()
    this.callList()
    this.fetchTotalPages()
    this.loadMemberUsers()
  },

  methods: {
    loadAccessContext () {
      return getAccessContext()
        .then((context) => {
          this.accessContext = context
        })
        .catch((error) => {
          console.error(
            '案件画面の権限情報を取得できませんでした:',
            safeErrorDigest(error))
          this.accessContext = null
        })
    },
    async callList () {
      this.globalLoading = true
      this.errors = []
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_ORIGIN}/project`,
          {
            params: {
              currentPage: this.currentPage,
              pageSize: this.pageSize,
              keyword: this.keyword || undefined
            }
          }
        )
        this.project = Array.isArray(response.data) ? response.data : []
        this.emptyNote = this.project.length ? '' : ConstList.EMPTY
      } catch (error) {
        this.emptyNote = ''
        this.errors = ['プロジェクト情報を取得できませんでした。']
        this.reportError(error)
      } finally {
        this.globalLoading = false
      }
    },

    async loadMemberUsers () {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_ORIGIN}/project-members/users`
        )
        this.memberUsers = Array.isArray(response.data) ? response.data : []
        if (
          this.selectedMemberUserId &&
          !this.memberUsers.some(
            member => Number(member.id) === Number(this.selectedMemberUserId)
          )
        ) {
          this.selectedMemberUserId = ''
          this.memberProjects = []
        }
      } catch (error) {
        this.errors = ['所属変更の対象社員を取得できませんでした。']
        this.reportError(error)
      }
    },

    async fetchSelectedMemberProjects () {
      const response = await axios.get(
        `${process.env.VUE_APP_API_ORIGIN}/project-members/${this.selectedMemberUserId}`
      )
      return Array.isArray(response.data) ? response.data : []
    },

    async loadSelectedMemberProjects () {
      if (!this.selectedMemberUserId) {
        this.memberProjects = []
        return
      }
      this.membershipSaving = true
      try {
        this.memberProjects = await this.fetchSelectedMemberProjects()
      } catch (error) {
        this.memberProjects = []
        this.errors = ['選択した社員の所属プロジェクトを取得できませんでした。']
        this.reportError(error)
      } finally {
        this.membershipSaving = false
      }
    },

    isMemberAssigned (projectId) {
      return this.memberProjects.some(
        item => Number(item.id) === Number(projectId)
      )
    },

    async onMemberToggle (item, nowChecked) {
      this.$delete(this.rowMessages, item.id)
      if (!this.selectedMemberUserId) {
        this.$set(this.rowMessages, item.id, { type: 'danger', text: '所属を変更する社員を選択してください。' })
        return
      }

      const previousProjects = this.memberProjects.slice()
      const nextProjects = nowChecked
        ? (
          previousProjects.some(project => Number(project.id) === Number(item.id))
            ? previousProjects
            : [...previousProjects, { id: item.id, name: item.name }]
        )
        : previousProjects.filter(
          project => Number(project.id) !== Number(item.id)
        )
      const projectIds = nextProjects.map(project => Number(project.id))

      this.membershipSaving = true
      try {
        try {
          await axios.post(
            `${process.env.VUE_APP_API_ORIGIN}/project-members/${this.selectedMemberUserId}`,
            { projectIds },
            { headers: { 'Content-Type': 'application/json' } }
          )
        } catch (saveError) {
          this.memberProjects = previousProjects
          this.$set(this.rowMessages, item.id, { type: 'danger', text: '選択した社員の所属プロジェクトを更新できませんでした。' })
          this.reportError(saveError)
          return
        }

        // POST成功時点で保存済み。再取得失敗で保存前の状態へ戻さない。
        this.memberProjects = nextProjects
        try {
          this.memberProjects = await this.fetchSelectedMemberProjects()
        } catch (refreshError) {
          this.$set(this.rowMessages, item.id, {
            type: 'danger',
            text: '所属は保存されましたが、確認結果を再取得できませんでした。画面を再読み込みしてください。'
          })
          this.reportError(refreshError)
        }
      } finally {
        this.membershipSaving = false
      }
    },

    goProject (id) {
      const row = this.project.find(
        item => Number(item.id) === Number(id)
      )
      if (row) {
        this.$router.push({
          name: this.projectInfo,
          params: { value: row }
        }).catch(() => {})
        return
      }
      this.$router.push({
        name: this.projectInfo,
        params: { projectId: id }
      }).catch(() => {})
    },

    openModal (item) {
      if (this.submit_flg) return
      this.postItem = item
      this.showModal = true
    },

    async deleteProject () {
      if (!this.postItem || !this.postItem.id) return
      this.loading = true
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API_ORIGIN}/projectDelete`,
          { id: this.postItem.id },
          { headers: { 'Content-Type': 'application/json' } }
        )
        const count = Number(response.data || 0)
        if (count > 0) {
          alert(`プロジェクトを設定中の方が${count}人います。`)
        } else {
          alert(ConstList.DELETE_MESSAGE)
          this.showModal = false
        }
        await Promise.all([
          this.callList(),
          this.fetchTotalPages(),
          this.loadSelectedMemberProjects()
        ])
      } catch (error) {
        alert(requestFailureMessage(error))
        this.reportError(error)
      } finally {
        this.loading = false
      }
    },

    closeModal () {
      this.showModal = false
    },

    /** 削除した案件を戻したあと、一覧だけを読み直す（画面ごと読み直すと、戻せたという文も節も消える）。 */
    reloadAfterRestore () {
      this.callList()
      this.fetchTotalPages()
    },

    changePage (page) {
      this.currentPage = page
      this.callList()
    },

    /**
     * 絞り込みを受ける。
     *
     * **ページを1へ戻す。**5ページ目のまま絞り込むと、結果が1ページ
     * しか無いときに空の表になる。件数も先に数え直す。
     */
    applyKeyword (keyword) {
      this.keyword = keyword
      this.currentPage = 1
      Promise.resolve(this.fetchTotalPages()).then(() => this.callList())
    },

    async fetchTotalPages () {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_ORIGIN}/projectCount`,
          { params: { keyword: this.keyword || undefined } }
        )
        this.totalCount = Number(response.data || 0)
        this.totalPages = Math.max(1, Math.ceil(this.totalCount / this.pageSize))
      } catch (error) {
        this.reportError(error)
      }
    },

    reportError (error) {
      axios
        .post(
          `${process.env.VUE_APP_API_ORIGIN}/error/frontError`,
          { error: String(error) },
          { headers: { 'Content-Type': 'application/json' } }
        )
        .catch(() => {})
    }
  }
}
</script>

<style scoped>
th,
td {
  white-space: nowrap;
}

td {
  vertical-align: middle;
  padding-top: 0;
  padding-bottom: 0;
}
</style>
